import { QuerySelect } from '../../QuerySelect';
import { useLenders, useLoanQuotes } from '@axo/shared/data-access/hooks';
import { useCallback, useState } from 'react';
import styles from './SelectQuote.module.css';
import { loan_quote } from '@axo/shared/data-access/types';
import { Icon } from '@blueprintjs/core';

export type ILender = {
  ID: string;
  shortName: string;
};

export type IQuote = {
  ID: string;
  status: loan_quote.LoanQuoteStatus | undefined;
};

export type ISelectQuote = {
  applicationID: string;
  marketCountry: string;
  onChange: (loanQuoteID: string | undefined) => void;
};

export function SelectQuote({
  applicationID,
  marketCountry,
  onChange,
}: ISelectQuote) {
  const loanQuoteQuery = useLoanQuotes(applicationID);
  const allLenders = useLenders(marketCountry);
  const [selectedLender, setSelectedLender] = useState<ILender>();
  const [selectedQuote, setSelectedQuote] = useState<IQuote>();

  const resolveBankName = useCallback(
    (id: string | undefined) =>
      allLenders?.data?.find(({ ID }) => ID === id)?.ShortName || '-',
    [allLenders.data]
  );

  function getLenders() {
    const lenders = new Set<ILender>();
    loanQuoteQuery?.data?.forEach((quote) => {
      const lenderAdded = [...lenders].find(
        (lender) => lender.ID === quote.LenderID
      );
      if (!lenderAdded) {
        lenders.add({
          ID: quote.LenderID,
          shortName: resolveBankName(quote.LenderID),
        });
      }
    });
    const lendersArray = [...lenders];
    return lendersArray;
  }

  function getQuotes(): IQuote[] | undefined {
    return loanQuoteQuery?.data
      ?.filter((quote) => quote.LenderID === selectedLender?.ID)
      .map((filteredQuote) => {
        return { ID: filteredQuote.ID, status: filteredQuote.Status };
      });
  }

  function handleLenderChange(lender: ILender) {
    setSelectedLender(lender);
    setSelectedQuote(undefined);
    onChange(undefined);
  }

  function handleQuoteChange(quote: IQuote) {
    setSelectedQuote(() => quote);
    onChange(quote.ID);
  }

  return (
    <>
      <p>Link to a loan quote:</p>
      <div className={styles.container}>
        <QuerySelect<ILender>
          data={getLenders()}
          onSelect={handleLenderChange}
          stringify={(lender) => lender.shortName}
          value={selectedLender}
          fill={true}
          className={styles.button}
        />
        <Icon icon="git-commit" size={24} className={styles.icon} />
        <QuerySelect<IQuote>
          data={getQuotes() ?? []}
          onSelect={handleQuoteChange}
          stringify={(quote) => quote.status ?? ''}
          value={selectedQuote}
          fill={true}
          className={styles.button}
          disabled={!selectedLender}
        />
      </div>
    </>
  );
}
